import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DoneOutlined as DoneOutlinedIcon
} from '@mui/icons-material'

import {
  BorderLineSuccess,
  Button,
  Span,
  Input,
  Row,
  Form,
  PasswordRequerimentsChecklist
} from 'components'
import { useToggle } from 'hooks'
import { COLORS, FONTS, SPACING, TYPOGRAPHY, VALIDATIONS } from 'utils'
import { passwordValidator } from 'utils/passwordValidator.utils'
import { yup, yupResolver } from 'utils/yup'

export interface PasswordProps {
  newPassword: string
  confirmNewPassword: string
}

const schema = yup
  .object({
    newPassword: yup.string().required('Informe nova senha.').password(),
    confirmNewPassword: yup
      .string()
      .required('Informar Confirmar Senha')
      .test({
        message: 'Senhas se diferem',
        name: 'equalsTo',
        params: {},
        test: function (this: any, value: string) {
          return value == this.parent.newPassword
        }
      })
  })
  .required()

const MAX_PASSWORD_LENGTH = 8

export const ChangePasswordModal = ({
  open,
  handleClose,
  handleChangePassword
}) => {
  const { control, handleSubmit, formState, watch, setError, clearErrors } =
    useForm<PasswordProps>({
      resolver: yupResolver(schema)
    })

  const [showPassword, toggleShowPassword] = useToggle()
  const { isPasswordModelValid } = passwordValidator()

  const watchNovaSenha = watch('newPassword')
  const watchNovaSenhaConfirm = watch('confirmNewPassword')

  const handleSubmitForm = (data) => {
    clearErrors()

    if (
      !isPasswordModelValid(watchNovaSenha, 'newPassword', setError) ||
      !isPasswordModelValid(
        watchNovaSenhaConfirm,
        'confirmNewPassword',
        setError
      )
    ) {
      return
    }

    if (watchNovaSenha != watchNovaSenhaConfirm) {
      setError('confirmNewPassword', {
        message: 'Senhas se diferem'
      })
      return
    }

    if (watchNovaSenha) {
      handleChangePassword(data)
    }
  }

  const getPasswordRequerimentSatisfiedColor = (satisfied: boolean) => {
    if (watchNovaSenha?.length === 0) {
      return 'black'
    }

    if (!satisfied) {
      return COLORS.error
    }

    return COLORS.success
  }

  const passwordMatchIcon = () => {
    if (
      watchNovaSenha !== watchNovaSenhaConfirm ||
      !watchNovaSenha ||
      !watchNovaSenhaConfirm
    ) {
      return null
    }

    if (
      watchNovaSenhaConfirm?.length < MAX_PASSWORD_LENGTH - 1 ||
      watchNovaSenha?.length < MAX_PASSWORD_LENGTH - 1
    ) {
      return null
    }

    return <DoneOutlinedIcon cursor="default" color="success" />
  }

  const disabledSaved = (novaSenha: string, novaSenhaConfirm: string) => {
    if (
      !VALIDATIONS.minCharacters(novaSenha, 8) &&
      !VALIDATIONS.minCharacters(novaSenhaConfirm, 8)
    ) {
      return true
    }
    return false
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <BorderLineSuccess colorLine={COLORS.primary} />
      <DialogTitle>
        <Span
          text="Alterar Senha"
          fontSize={TYPOGRAPHY.subtitleSize20}
          fontWeight={FONTS.bold}
        />
      </DialogTitle>
      <DialogContent>
        <Form onSubmit={handleSubmit(handleSubmitForm)}>
          <Row marginBottom={SPACING.spacing30}>
            <Input
              label="Nova Senha"
              control={control}
              defaultValue=""
              type={showPassword ? 'none' : 'password'}
              onClickIcon={toggleShowPassword}
              inputIcon={
                showPassword ? (
                  <VisibilityOffIcon cursor="pointer" />
                ) : (
                  <VisibilityIcon cursor="pointer" />
                )
              }
              errors={formState.errors}
              formValue="newPassword"
            />
            <Row marginRight={SPACING.spacing30}></Row>
            <Input
              label="Confirmar Nova Senha"
              control={control}
              defaultValue=""
              type={'password'}
              inputIcon={passwordMatchIcon()}
              errors={formState.errors}
              formValue="confirmNewPassword"
            />
          </Row>

          <PasswordRequerimentsChecklist password={watchNovaSenha}>
            {(requeriments) =>
              requeriments.map((requeriment) => (
                <Span
                  key={requeriment.id}
                  text={requeriment.description}
                  fontSize={TYPOGRAPHY.captionSize12}
                  color={
                    watchNovaSenha
                      ? getPasswordRequerimentSatisfiedColor(
                          requeriment.satisfied
                        )
                      : 'black'
                  }
                />
              ))
            }
          </PasswordRequerimentsChecklist>

          <Row justifyContent="flex-end" marginBottom={SPACING.spacing20}>
            <Button
              text="Cancelar"
              variantButton="outlined"
              backgroundColor={COLORS.white}
              textColor={COLORS.black}
              marginRight={SPACING.spacing20}
              borderColor={COLORS.black}
              onClick={handleClose}
            />
            <Button
              text="Salvar"
              type="submit"
              disabled={disabledSaved(watchNovaSenha, watchNovaSenhaConfirm)}
            />
          </Row>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
